import { ref, onMounted, onUnmounted } from 'vue'
import { versionService } from '../services/index.js'

// State partagé entre tous les composants
const currentVersion = ref(null)
const hasNewVersion = ref(false)
const isChecking = ref(false)
// Dernière version annoncée par le serveur, et celle que l'utilisateur a
// écartée d'un « Plus tard » : sans cette mémoire, le sondage suivant (2 min)
// reposait le bandeau, et fermer la notification ne servait à rien.
const availableVersion = ref(null)
const dismissedVersion = ref(null)
let checkInterval = null

// Intervalle de vérification (2 minutes en production)
const CHECK_INTERVAL = 2 * 60 * 1000

export function useVersionCheck() {
  
  const fetchVersion = async () => {
    try {
      const data = await versionService.check()
      return data.version
    } catch (error) {
      console.error('Failed to fetch version:', error)
    }
    return null
  }

  const checkForUpdates = async () => {
    if (isChecking.value) return
    isChecking.value = true
    
    try {
      const serverVersion = await fetchVersion()
      
      if (serverVersion) {
        if (!currentVersion.value) {
          currentVersion.value = serverVersion
        } else if (serverVersion !== currentVersion.value) {
          availableVersion.value = serverVersion
          // Une version déjà écartée ne redemande pas la parole ; une version
          // encore plus récente, si.
          hasNewVersion.value = serverVersion !== dismissedVersion.value
        }
      }
    } finally {
      isChecking.value = false
    }
  }

  const reloadApp = () => {
    // Force un rechargement complet (pas depuis le cache)
    window.location.reload(true)
  }

  const dismissUpdate = () => {
    hasNewVersion.value = false
    // On retient la version écartée : l'utilisateur ne sera re-sollicité que
    // par une version encore plus récente (ou au prochain chargement de l'app).
    dismissedVersion.value = availableVersion.value
  }

  const startVersionCheck = () => {
    // Vérifier immédiatement
    checkForUpdates()
    
    // Puis vérifier périodiquement
    if (!checkInterval) {
      checkInterval = setInterval(checkForUpdates, CHECK_INTERVAL)
    }
  }

  const stopVersionCheck = () => {
    if (checkInterval) {
      clearInterval(checkInterval)
      checkInterval = null
    }
  }

  // Vérifier aussi quand l'utilisateur revient sur l'onglet
  const handleVisibilityChange = () => {
    if (!document.hidden && currentVersion.value) {
      checkForUpdates()
    }
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    currentVersion,
    availableVersion,
    hasNewVersion,
    isChecking,
    checkForUpdates,
    reloadApp,
    dismissUpdate,
    startVersionCheck,
    stopVersionCheck
  }
}
